import { Element } from '../Element';
import { AudioElement } from './AudioElement';
import { ImageElement } from './ImageElement';
import { ShapeElement } from './ShapeElement';
import { TextElement } from './TextElement';
import { VideoElement } from './VideoElement';

/**
 * CompositionElement represents a composition on the timeline
 * Aligned with Creatomate properties and structure
 *
 * A composition is a container for other elements
 */

export class CompositionElement extends Element {
  private _flow_direction: string = 'vertical';
  private _loop: boolean = false;
  private _plays: number | null = null;
  private _elements: Element[] = [];

  constructor(props: Partial<CompositionElement> | Record<string, any> = {}) {
    super({ ...props, type: 'composition' });

    // Set composition-specific properties
    this._flow_direction = props.flow_direction ?? 'vertical';
    this._loop = props.loop ?? false;
    this._plays = props.plays ?? null;

    // Create a copy of the elements array to avoid readonly issues
    this._elements = props.elements
      ? CompositionElement.childElementsFromJSON(props.elements)
      : [];
  }

  // Getters and setters
  get flow_direction(): string {
    return this._flow_direction;
  }

  set flow_direction(value: string) {
    this._flow_direction = value;
  }

  get loop(): boolean {
    return this._loop;
  }

  set loop(value: boolean) {
    this._loop = value;
  }

  get plays(): number | null {
    return this._plays;
  }

  set plays(value: number | null) {
    this._plays = value;
  }

  get elements(): readonly Element[] {
    return [...this._elements]; // Return a copy to prevent direct mutation
  }

  /**
   * Adds an element to this composition
   * @param element The element to add
   */
  addElement(element: Element): void {
    this._elements.push(element);
  }

  /**
   * Removes an element from this composition
   * @param elementId The ID of the element to remove
   * @returns true if the element was found and removed, false otherwise
   */
  removeElement(elementId: string): boolean {
    const initialLength = this._elements.length;
    this._elements = this._elements.filter(el => el.id !== elementId);
    return this._elements.length !== initialLength;
  }

  /**
   * Gets an element by ID
   * @param elementId The ID of the element to get
   * @returns The element with the specified ID, or undefined if not found
   */
  getElementById(elementId: string): Element | undefined {
    return this._elements.find(el => el.id === elementId);
  }

  /**
   * Creates a deep clone of this composition element
   * Override the base Element.clone() to ensure we create a CompositionElement, not a generic Element
   */
  override clone(): CompositionElement {
    const el = new CompositionElement({
      ...this.getState(),
      // Ensure specific composition properties are copied
      flow_direction: this._flow_direction,
      loop: this._loop,
      plays: this._plays,
      // Clone all child elements
      elements: this._elements.map(element => element.clone()),
    });

    return el;
  }

  protected override getAdditionalProperties(): Record<string, any> {
    // Only include properties in the JSON if they have non-default values
    const props: Record<string, any> = {
      // Include elements array if not empty
      ...(this._elements.length > 0 && {
        elements: this._elements.map(element => element.toJSON()),
      }),
    };

    // Only include flow_direction if it's not the default
    if (this._flow_direction !== 'vertical') {
      props.flow_direction = this._flow_direction;
    }

    // Only include loop if it's true (default is false)
    if (this._loop) {
      props.loop = this._loop;
    }

    // Only include plays if it's not null
    if (this._plays !== null) {
      props.plays = this._plays;
    }

    return props;
  }

  static childElementsFromJSON(jsonArray: any[]): Element[] {
    const elements: Element[] = [];
    for (const elementJson of jsonArray) {
      if (!elementJson.type) {
        elements.push(Element.fromJSON(elementJson));
        continue;
      }

      let element: Element;
      switch (elementJson.type) {
        case 'video':
          element = VideoElement.fromJSON(elementJson);
          break;
        case 'text':
          element = TextElement.fromJSON(elementJson);
          break;
        case 'audio':
          element = AudioElement.fromJSON(elementJson);
          break;
        case 'image':
          element = ImageElement.fromJSON(elementJson);
          break;
        case 'shape':
          element = ShapeElement.fromJSON(elementJson);
          break;
        case 'composition':
          element = CompositionElement.fromJSON(elementJson);
          break;
        default:
          element = Element.fromJSON(elementJson);
      }
      elements.push(element);
    }
    return elements;
  }

  /**
   * Creates a CompositionElement from JSON data
   */
  static override fromJSON(json: Record<string, any>): CompositionElement {
    // Parse time-based properties that may be formatted as strings
    const parsedTime = Element.parseTimeProperty(
      json.time ?? json.start ?? json.globalTime ?? 0,
    );
    const parsedDuration = Element.parseTimeProperty(json.duration);

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    // Create child elements if present
    const childElements: Element[] = this.childElementsFromJSON(
      json.elements ?? [],
    );

    return new CompositionElement({
      // Base element properties
      id: json.id,
      track: json.track ?? null,
      time: timeValue,
      duration: parsedDuration,
      type: 'composition', // Force composition type
      dynamic: json.dynamic ?? false,
      locked: json.locked ?? false,
      visible: json.visible ?? true,
      x: json.x ?? '50%',
      y: json.y ?? '50%',
      x_anchor: json.x_anchor ?? '50%',
      y_anchor: json.y_anchor ?? '50%',
      width: json.width ?? '100%',
      height: json.height ?? '100%',
      z_index: json.z_index ?? null,
      clip: json.clip ?? false,

      // Animation support (now handled by Element)
      animations: Array.isArray(json.animations) ? json.animations : [],
      color_overlay: json.color_overlay ?? null, // dip and crossfade animations

      // Composition-specific properties
      flow_direction: json.flow_direction ?? 'vertical',
      loop: json.loop ?? false,
      plays: json.plays ?? null,
      elements: childElements,
    });
  }

  /**
   * Captures the mutable state of the composition element.
   * Extends the base Element.getState() to include composition-specific properties.
   * @returns A record of all mutable properties.
   */
  override getState(): Record<string, any> {
    // Get the base element state first
    const baseState = super.getState();

    // Add composition-specific properties
    return {
      ...baseState,
      flow_direction: this._flow_direction,
      loop: this._loop,
      plays: this._plays,
      elements: this._elements.map(element => element.getState()),
    };
  }

  /**
   * Restores the composition element's mutable state from a state record.
   * Extends the base Element.restoreState() to handle composition-specific properties.
   * @param state The state to restore.
   */
  override restoreState(state: Record<string, any>): void {
    // First, restore base element properties
    super.restoreState(state);

    // Then restore composition-specific properties
    if (state.flow_direction !== undefined)
      this._flow_direction = state.flow_direction;
    if (state.loop !== undefined) this._loop = state.loop;
    if (state.plays !== undefined) this._plays = state.plays;

    // Restore elements if provided
    if (state.elements !== undefined && Array.isArray(state.elements)) {
      // This implementation is simplified - in reality you'd need to properly restore
      // each element based on its type, similar to fromJSON
      this._elements = state.elements.map((elementState: any) => {
        const element = new Element();
        element.restoreState(elementState);
        return element;
      });
    }
  }

  findElement(predicate: (el: Element) => boolean): Element | undefined {
    for (const nestedElement of this.elements) {
      if (predicate(nestedElement)) {
        return nestedElement;
      }
      if (Element.isCompositionElement(nestedElement)) {
        const found = nestedElement.findElement(predicate);
        if (found) {
          return found;
        }
      }
    }
  }

  /**
   * Checks if this composition element is a Black Frame composition.
   * Black Frame compositions have exactly:
   * - 1 image element with fit: 'contain'
   * - 1 black shape element
   */
  isBlackFrameComposition(): boolean {
    const elements = this.elements;

    if (elements.length !== 2) {
      return false;
    }

    // Look for an image element and a shape element
    const imageElement = elements.find(el =>
      Element.isImageElement(el),
    ) as ImageElement;
    const shapeElement = elements.find(el => Element.isShapeElement(el));

    if (!imageElement || !shapeElement) {
      return false;
    }

    // Check if image has contain fit (typical for Black Frame)
    if (imageElement.fit !== 'contain') {
      return false;
    }

    // Additional check: shape should be black
    const shapeProps = shapeElement.toJSON();
    if (
      shapeProps.fill_color !== '#000' &&
      shapeProps.fill_color !== '#000000'
    ) {
      return false;
    }

    return true;
  }
}
